import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTournamentDetails } from '../../store/slices/tournamentSlice';
import { fetchMyTeams, updateTeamPlayers, createEnrollmentPreference } from '../../store/slices/teamSlice';
import { ShieldCheck, ShieldAlert, CreditCard, ChevronLeft, Users, Trophy, Save } from 'lucide-react';

const MyTeam = () => {
    const { slug, id, teamId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { tournamentDetails } = useSelector(state => state.tournaments);
    const { myTeams, loading, preferenceUrl } = useSelector(state => state.teams);

    const [players, setPlayers] = useState([{ name: '', dni: '' }]);

    // Identificar cuál equipo estamos gestionando
    const myTeam = teamId
        ? myTeams?.find(t => t._id === teamId)
        : myTeams?.[0]; // Fallback por si entran sin ID

    useEffect(() => {
        dispatch(fetchTournamentDetails({ slug, id }));
        dispatch(fetchMyTeams(id));
    }, [dispatch, slug, id]);

    useEffect(() => {
        if (myTeam?.players?.length > 0) {
            setPlayers(myTeam.players);
        } else {
            // Un par de slots vacíos por defecto
            setPlayers([{ name: '', dni: '' }, { name: '', dni: '' }, { name: '', dni: '' }]);
        }
    }, [myTeam]);

    useEffect(() => {
        if (preferenceUrl) {
            window.location.href = preferenceUrl;
        }
    }, [preferenceUrl]);

    const handlePlayerChange = (index, field, value) => {
        const newPlayers = [...players];
        newPlayers[index][field] = value;
        setPlayers(newPlayers);
    };

    const addPlayerRow = () => {
        setPlayers([...players, { name: '', dni: '' }]);
    };

    const removePlayerRow = (index) => {
        setPlayers(players.filter((_, i) => i !== index));
    };

    const handleSavePlayers = (e) => {
        e.preventDefault();
        if (!myTeam) return;
        const validPlayers = players.filter(p => p.name.trim() !== '');
        dispatch(updateTeamPlayers({ teamId: myTeam._id, players: validPlayers }));
    };

    const handlePay = () => {
        if (!myTeam) return;
        dispatch(createEnrollmentPreference({ tournamentId: id, teamName: myTeam.name }));
    };

    if (loading && (!myTeams || myTeams.length === 0)) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!myTeam) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center text-white">
                <div className="text-center">
                    <Trophy className="mx-auto mb-4 opacity-20" size={60} />
                    <p className="text-xl font-bold mb-4">No tienes un equipo inscrito en este torneo con ese ID.</p>
                    <button onClick={() => navigate(`/${slug}/tournaments/${id}`)} className="text-blue-400 font-bold hover:underline">Volver al Torneo</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#080808] text-white">
            <header className="border-b border-white/10 bg-black/50 sticky top-0 z-10 backdrop-blur-md">
                <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
                    <button onClick={() => navigate(`/${slug}/tournaments/${id}`)} className="flex items-center gap-2 text-white/40 hover:text-white transition-colors">
                        <ChevronLeft size={18} /> <span className="text-xs font-black uppercase tracking-widest">Torneo</span>
                    </button>
                    {tournamentDetails && (
                        <span className="text-xs font-bold text-white/50">{tournamentDetails.name}</span>
                    )}
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-6 py-12">
                <div className="flex flex-col md:flex-row items-center gap-6 justify-between mb-12 border-b border-white/10 pb-12">
                    <div>
                        <h1 className="text-5xl font-black mb-2 tracking-tighter"><span className="text-white/30 text-3xl font-light">Equipo: </span>{myTeam.name}</h1>
                        <p className="text-white/40 font-medium">Gestioná tu inscripción y lista de buena fe</p>
                    </div>

                    <div className="flex flex-col items-end gap-3 shrink-0">
                        {myTeam.paymentStatus === 'approved' ? (
                            <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-400 px-6 py-3 rounded-full font-black text-sm uppercase tracking-widest">
                                <ShieldCheck size={18} /> PAGO CONFIRMADO
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 px-6 py-3 rounded-full font-black text-sm uppercase tracking-widest">
                                <ShieldAlert size={18} /> PENDIENTE DE PAGO
                            </div>
                        )}

                        {myTeam.paymentStatus !== 'approved' && (
                            <button
                                onClick={handlePay}
                                disabled={loading}
                                className="w-full flex justify-center items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-black px-6 py-3 rounded-xl shadow-lg shadow-blue-500/20 transition-all disabled:opacity-50"
                            >
                                {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div> : <><CreditCard size={18} /> PAGAR INSCRIPCIÓN</>}
                            </button>
                        )}
                    </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-[32px] p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />

                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-2xl font-black flex items-center gap-3"><Users className="text-blue-500" size={24} /> Lista de Buena Fe</h2>
                            <p className="text-sm text-white/40 mt-1">Ingresá los nombres de los jugadores que representarán al equipo.</p>
                        </div>
                    </div>

                    <form onSubmit={handleSavePlayers}>
                        <div className="space-y-4 mb-8">
                            {players.map((player, index) => (
                                <div key={index} className="flex flex-col md:flex-row gap-4 bg-black/40 p-4 rounded-2xl border border-white/5 group transition-colors focus-within:border-blue-500/50">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-white/40 font-black text-xs shrink-0 self-center">
                                        {index + 1}
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Nombre y Apellido"
                                        value={player.name}
                                        onChange={(e) => handlePlayerChange(index, 'name', e.target.value)}
                                        className="flex-1 bg-transparent text-white font-bold outline-none placeholder:text-white/20"
                                    />
                                    <input
                                        type="text"
                                        placeholder="DNI"
                                        value={player.dni}
                                        onChange={(e) => handlePlayerChange(index, 'dni', e.target.value)}
                                        className="w-full md:w-40 bg-white/5 md:bg-transparent rounded-xl md:rounded-none px-4 md:px-0 py-2 md:py-0 text-white font-medium outline-none placeholder:text-white/20 text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removePlayerRow(index)}
                                        className="md:opacity-0 group-hover:opacity-100 text-red-500/50 hover:text-red-500 font-bold text-sm px-2 transition-all self-center"
                                    >
                                        Quitar
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col md:flex-row gap-4 items-center justify-between pt-6 border-t border-white/5">
                            <button
                                type="button"
                                onClick={addPlayerRow}
                                className="text-sm font-bold text-white/40 hover:text-white transition-colors"
                            >
                                + Agregar otro jugador
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full md:w-auto flex items-center justify-center gap-2 bg-white text-black font-black px-8 py-4 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                                {loading ? <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin"></div> : <><Save size={18} /> GUARDAR LISTA</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MyTeam;
